// Story sponsorship (research): one company pays to be named as sponsor of the
// research behind an admin's News Alert and its story.

// The API accepts only these, in pence.
export const RESEARCH_PRICES = [10000, 25000, 50000, 75000, 100000];
const DEFAULT_PRICE = 50000;

export const priceLabel = (pence) =>
    new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
        minimumFractionDigits: pence % 100 ? 2 : 0,
    }).format(pence / 100);

// The admin's whole intro paragraph; the API allows the same.
export const RESEARCH_TEXT_MAX = 600;

export const RESEARCH_TEXT_TOO_LONG = `Shorten the sponsorship text to ${RESEARCH_TEXT_MAX} characters.`;

// Like the API, a text saved before the limit may stay as it is; a changed one must fit.
export const researchTextTooLong = (draft, storedText) => {
    const text = (draft?.researchDetail || '').trim();
    return text.length > RESEARCH_TEXT_MAX && text !== (storedText ?? '');
};

export const researchOfferDraft = (row) => ({
    enabled: Boolean(row?.enabled),
    researchDetail: row?.researchDetail ?? '',
    pricePence: row?.pricePence ?? DEFAULT_PRICE,
});

// The API refuses any change once a buyer has reserved or paid.
export const researchOfferLocked = (row) =>
    Boolean(row) && row.status !== 'available';

export const researchAdminStatus = (row) => {
    if (row?.status === 'sold') return `Sold to ${row.sponsorName || '…'}`;
    if (row?.status === 'reserved') {
        return `Checkout in progress (${row.sponsorName || 'buyer'})`;
    }
    return null;
};

// Caught before any request, so a create never leaves half a save behind.
export const researchOfferError = (draft, storedText = null) => {
    if (!draft?.enabled) return null;
    if (!draft.researchDetail.trim()) {
        return 'Write the sponsorship text before offering the story sponsorship.';
    }
    return researchTextTooLong(draft, storedText)
        ? RESEARCH_TEXT_TOO_LONG
        : null;
};

// Without a detail the API keeps the stored one, so switching off loses nothing.
export const researchOfferBody = (draft) => {
    const researchDetail = draft.researchDetail.trim();
    return {
        enabled: draft.enabled,
        pricePence: draft.pricePence,
        ...(researchDetail ? { researchDetail } : {}),
    };
};

// Same rule as the API: an enabled offer, or one that is reserved or sold, keeps the
// alert's story sponsor and expert answers off.
export const researchKeepsSlotsOff = (row) =>
    Boolean(row && (row.enabled || row.status !== 'available'));

// Only a real change is saved: an untouched alert has nothing to write.
export const researchOfferChanged = (draft, row) => {
    if (researchOfferLocked(row)) return false;
    if (!row) return draft.enabled;
    return (
        draft.enabled !== Boolean(row.enabled) ||
        draft.pricePence !== row.pricePence ||
        draft.researchDetail.trim() !== (row.researchDetail ?? '')
    );
};

// What the public box shows. A buyer holding the reservation may go back to
// their own checkout, so for them a reserved slot still shows the form.
export const researchBoxView = (live, hasToken = false) => {
    if (!live?.enabled) return null;
    if (live.status === 'sold') return 'sold';
    if (live.status === 'reserved' && !hasToken) return 'reserved';
    return 'available';
};

const RESERVE_ERRORS = {
    IN_PROGRESS: 'Your checkout is being prepared, try again in a moment.',
    RESERVED: 'Sponsorship being taken, check back shortly.',
    SOLD: 'This research has already been sponsored.',
    NOT_OFFERED: 'This research is no longer offered for sponsorship.',
};

export const reserveErrorMessage = (response) => {
    const known = RESERVE_ERRORS[response?.data?.code];
    if (known) return known;
    if (response?.status === 429) {
        return 'Too many attempts, try again in a minute.';
    }
    if (response?.status === 412) {
        return 'Our spam check is unavailable, try again shortly.';
    }
    if (response?.status === 503) {
        return 'Sponsorship is not available right now. Please try again later.';
    }
    const message = response?.data?.message;
    if ([400, 403, 409].includes(response?.status) && message) {
        return Array.isArray(message) ? message.join(' ') : message;
    }
    return 'Something went wrong. Please try again.';
};

const storageKey = (alertId) => `research-sponsor-${alertId}`;

// A Stripe session lasts 33 minutes; past this nothing stored can be resumed.
export const RESERVATION_MAX_AGE_MS = 35 * 60 * 1000;

// Storage can be blocked (private mode, site data off); the buyer then simply
// can't resume or release, and the slot frees itself when Stripe expires it.
const withStorage = (type, use) => {
    try {
        return use(window[type]);
    } catch {
        return null;
    }
};

const fresh = (raw, now) => {
    try {
        const value = JSON.parse(raw);
        const age = now - value.savedAt;
        return age >= 0 && age < RESERVATION_MAX_AGE_MS ? value : null;
    } catch {
        return null;
    }
};

// The token lives in localStorage, so the cancel page can release it whatever
// tab Stripe returns to; what the buyer typed stays in this tab's sessionStorage.
// Returns { reservationToken, fields }, fields null unless complete; null when
// there is no fresh token.
export const parseStoredReservation = (rawToken, rawFields, now) => {
    const reservationToken = fresh(rawToken, now)?.reservationToken;
    if (typeof reservationToken !== 'string' || !reservationToken) {
        return null;
    }
    const fields = fresh(rawFields, now)?.fields;
    const complete =
        typeof fields?.sponsorName === 'string' &&
        fields.sponsorName.trim() &&
        typeof fields.email === 'string' &&
        fields.email.trim();
    return {
        reservationToken,
        fields: complete
            ? {
                  sponsorName: fields.sponsorName,
                  website:
                      typeof fields.website === 'string' ? fields.website : '',
                  email: fields.email,
              }
            : null,
    };
};

export const storedReservation = (alertId, now = Date.now()) =>
    parseStoredReservation(
        withStorage('localStorage', (s) => s.getItem(storageKey(alertId))),
        withStorage('sessionStorage', (s) => s.getItem(storageKey(alertId))),
        now
    );

export const storeReservation = (
    alertId,
    reservationToken,
    fields,
    savedAt = Date.now()
) => {
    withStorage('localStorage', (s) =>
        s.setItem(
            storageKey(alertId),
            JSON.stringify({ reservationToken, savedAt })
        )
    );
    withStorage('sessionStorage', (s) =>
        s.setItem(storageKey(alertId), JSON.stringify({ fields, savedAt }))
    );
};

export const forgetReservation = (alertId) => {
    withStorage('localStorage', (s) => s.removeItem(storageKey(alertId)));
    withStorage('sessionStorage', (s) => s.removeItem(storageKey(alertId)));
};
